import {
  ActionOutboxEntityType,
  CatalogResults,
  EntityCatalogData,
  Event,
  EventEntities,
  EventEntityCounts,
  EventWithData,
  Listing,
  ListingDetailDataField,
  ListingWithCatalogData,
  Performer,
  Sale,
  SaleWithCatalogData,
  TopLevelCategory,
  Venue,
} from 'src/WebApiController';

import {
  EntitySearchResult,
  SearchDataSummary,
  SearchDataWithEvent,
} from './SearchDataContext.types';

type EntityWithCatalogData = ListingWithCatalogData | SaleWithCatalogData;

/**
 * Builds an Event object from EntityCatalogData.
 * @param catalogData The catalog data containing event information
 * @param virtualId The viagogo virtual ID for the event
 * @returns An Event object or undefined if catalogData is null/undefined
 */
export function buildEventFromEntityCatalogData(
  catalogData: EntityCatalogData | null | undefined,
  virtualId: string
): Event | undefined {
  if (!catalogData?.eventName) {
    return undefined;
  }

  return {
    viagId: null,
    name: catalogData.eventName ?? '',
    shortName: catalogData.eventShortName ?? null,
    rowVer: null,
    posIds: [],
    posId: null,
    virtId: virtualId,
    oldVirtIds: null,
    mpId: null,
    dates: {
      start: catalogData.eventDateTime ?? null,
      end: null,
    },
    datesUtc: {
      start: null,
      end: null,
    },
    onSaleDate: null,
    rawOnSaleDate: null,
    rawPreSaleDate: null,
    evType: null,
    evStat: catalogData.eventStatus ?? null,
    perfId: catalogData.performerId ?? null,
    venId: catalogData.venueId ?? 0,
    venCfgId: null,
    genre: catalogData.eventGenre ?? TopLevelCategory.Concert,
    mapRejRsnId: null,
    mapRejDftTxt: null,
  };
}

/**
 * Builds a Performer object from EntityCatalogData.
 * @param catalogData The catalog data containing performer information
 * @returns A Performer object or undefined if performerId is not available
 */
export function buildPerformerFromEntityCatalogData(
  catalogData: EntityCatalogData | null | undefined
): Performer | undefined {
  if (catalogData?.performerId == null) {
    return undefined;
  }

  return {
    viagId: catalogData.performerId,
    name: catalogData.performerName ?? '',
    shortName: null,
    categ: [],
    genreId: null,
    grName: null,
  };
}

/**
 * Builds a Venue object from EntityCatalogData.
 * @param catalogData The catalog data containing venue information
 * @returns A Venue object or undefined if venueId is not available
 */
export function buildVenueFromEntityCatalogData(
  catalogData: EntityCatalogData | null | undefined
): Venue | undefined {
  if (catalogData?.venueId == null) {
    return undefined;
  }

  return {
    viagId: catalogData.venueId,
    name: catalogData.venueName ?? '',
    shortName: null,
    city: catalogData.venueCity ?? null,
    state: catalogData.venueStateProvince ?? null,
    ctryCode: catalogData.venueCountryCode ?? null,
    cap: null,
    excludeSearch: false,
  };
}

interface ExtractedEntities {
  items: EntityWithCatalogData[] | null;
  summary: EntityWithCatalogData | null;
  sample: EntityWithCatalogData | null;
}

/**
 * Extracts items, summary, and a sample entity from event data based on entity type.
 */
function extractEntitiesFromEventData(
  eventData: SearchDataWithEvent[string],
  entityType: ActionOutboxEntityType
): ExtractedEntities {
  if (entityType === ActionOutboxEntityType.Sale) {
    const items = eventData.sales;
    const summary = eventData.salesSummary;
    return {
      items,
      summary,
      sample: items?.[0] ?? summary,
    };
  }
  const items = eventData.listings;
  const summary = eventData.listingsSummary;
  return {
    items,
    summary,
    sample: items?.[0] ?? summary,
  };
}

/**
 * Builds an Event object from embedded catalog data or falls back to existing event data.
 */
function buildEventFromSampleCatalogData(
  sample: EntityWithCatalogData,
  viagogoVirtualId: string,
  eventFallback?: Event
): Event {
  const catalogData = sample.entityCatalogData;
  const builtEvent = buildEventFromEntityCatalogData(
    catalogData,
    viagogoVirtualId
  );

  if (builtEvent) {
    // Augment with sample-specific fields
    return {
      ...builtEvent,
      viagId: sample.viagEvId,
      posIds: sample.posEvId ? [sample.posEvId] : [],
      posId: sample.posEvId,
      mpId: sample.viagEvMapId,
    };
  }
  return eventFallback!;
}

/**
 * Builds and adds a Performer to catalog results from embedded data or fallback.
 */
function addPerformerToCatalogResults(
  sample: EntityWithCatalogData,
  catalogResults: CatalogResults,
  catalogDataFallback?: CatalogResults,
  eventFallback?: Event
): void {
  const catalogData = sample.entityCatalogData;
  const performer = buildPerformerFromEntityCatalogData(catalogData);

  if (performer && catalogData?.performerId != null) {
    catalogResults.performers[catalogData.performerId] = performer;
  } else if (eventFallback?.perfId != null) {
    const performerFallback =
      catalogDataFallback?.performers[eventFallback.perfId];
    if (performerFallback) {
      catalogResults.performers[eventFallback.perfId] = performerFallback;
    }
  }
}

/**
 * Builds and adds a Venue to catalog results from embedded data or fallback.
 */
function addVenueToCatalogResults(
  sample: EntityWithCatalogData,
  catalogResults: CatalogResults,
  catalogDataFallback?: CatalogResults,
  eventFallback?: Event
): void {
  const catalogData = sample.entityCatalogData;
  const venue = buildVenueFromEntityCatalogData(catalogData);

  if (venue && catalogData?.venueId != null) {
    catalogResults.venues[catalogData.venueId] = venue;
  } else if (eventFallback?.venId != null) {
    const venueFallback = catalogDataFallback?.venues[eventFallback.venId];
    if (venueFallback) {
      catalogResults.venues[eventFallback.venId] = venueFallback;
    }
  }
}

/**
 * Builds EventEntityCounts for either sales or listings.
 */
function buildEntityCounts(
  viagogoVirtualId: string,
  sample: EntityWithCatalogData,
  itemCount: number,
  entityType: ActionOutboxEntityType
): EventEntityCounts {
  const catalogData = sample.entityCatalogData;
  return {
    viagVirtId: viagogoVirtualId,
    perfId: catalogData?.performerId ?? null,
    venId: catalogData?.venueId ?? 0,
    rowVer: null,
    salesCnt: entityType === ActionOutboxEntityType.Sale ? itemCount : 0,
    listCnt: entityType === ActionOutboxEntityType.Listing ? itemCount : 0,
    pricedListCnt: 0,
    ungrListCnt: 0,
    listGrpCnt: 0,
    tktGrpsCnt: 0,
    poCnt: 0,
  };
}

/**
 * Builds EventEntities for either sales or listings.
 */
function buildEventEntities(
  eventData: SearchDataWithEvent[string],
  entityType: ActionOutboxEntityType,
  items: EntityWithCatalogData[] | null,
  summary: EntityWithCatalogData | null
): EventEntities {
  // For OpenSearch listings, purchase data is already embedded in the search result,
  // so mark dataIsLoaded[Purchase] = true so purchase columns (PurchasedBy, PO Date,
  // External PO ID) render without waiting for a secondary fetch that never happens in v2.
  const listings =
    entityType === ActionOutboxEntityType.Listing
      ? (items as Listing[] | null)?.map((l) => ({
          ...l,
          dataIsLoaded: {
            ...l.dataIsLoaded,
            [ListingDetailDataField.Purchase]: true,
          },
        })) ?? null
      : null;

  return {
    listings,
    listingsSummary:
      entityType === ActionOutboxEntityType.Listing
        ? (summary as Listing)
        : null,
    sales:
      entityType === ActionOutboxEntityType.Sale ? (items as Sale[]) : null,
    salesSummary:
      entityType === ActionOutboxEntityType.Sale ? (summary as Sale) : null,
    ticketGroups: null,
    ticketGroupsSummary: null,
    getDataFail: eventData.failedToRetrieveData ?? null,
  };
}

/**
 * Creates an empty CatalogResults object.
 */
function createEmptyCatalogResults(): CatalogResults {
  return {
    events: {},
    performers: {},
    venues: {},
    viagIdToLatestIdLookup: {},
    posIdToViagIdLookup: {},
    countries: {},
    purchaseCount: 0,
  };
}

/**
 * Generic function to build CatalogResults and EventWithData[] from SearchDataWithEvent.
 * Works for both sales and listings by extracting embedded catalog data.
 */
function buildCatalogAndEventsFromSearchData(
  searchResult: EntitySearchResult,
  searchDataSummary: SearchDataSummary | null | undefined,
  entityType: ActionOutboxEntityType,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
  indexLastUpdatedAt?: string | null;
  totalItemsCount?: number | null;
  pitId?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchAfter?: any[] | null;
} {
  const catalogResults = createEmptyCatalogResults();
  const eventsTransformed: EventWithData[] = [];

  const searchData = searchResult.data;

  // Indicates if the search data summary has been mapped to the event data below
  let isSearchDataSummaryMapped = false;

  // Use sortedEntityIds when present to preserve OpenSearch sort order.
  // Object.entries() reorders integer-like keys numerically per ECMAScript spec,
  // which would override the sort order returned by OpenSearch.
  const orderedIds = searchResult.sortedEntityIds ?? Object.keys(searchData);
  for (const viagogoVirtualId of orderedIds) {
    const eventData = searchData[viagogoVirtualId];
    if (!eventData) continue;
    const { items, summary, sample } = extractEntitiesFromEventData(
      eventData,
      entityType
    );

    if (!sample) {
      continue;
    }

    const eventFallback = catalogDataFallback?.events[viagogoVirtualId]?.event;

    if (!sample.entityCatalogData?.eventName && !eventFallback) {
      continue;
    }

    const event = buildEventFromSampleCatalogData(
      sample,
      viagogoVirtualId,
      eventFallback
    );

    addPerformerToCatalogResults(
      sample,
      catalogResults,
      catalogDataFallback,
      eventFallback
    );
    addVenueToCatalogResults(
      sample,
      catalogResults,
      catalogDataFallback,
      eventFallback
    );

    const itemCount = searchResult.totalItemsCount ?? items?.length ?? 0;
    const counts = buildEntityCounts(
      viagogoVirtualId,
      sample,
      itemCount,
      entityType
    );

    // If searchDataSummary is available, only use it. And only need to use it once
    // Any other cases, try to use the summary from the search data
    const summaryReconciled =
      searchDataSummary != null
        ? isSearchDataSummaryMapped
          ? null
          : entityType === ActionOutboxEntityType.Sale
            ? searchDataSummary.salesSummary
            : searchDataSummary.listingsSummary
        : summary;

    isSearchDataSummaryMapped = true;

    const entities = buildEventEntities(
      eventData,
      entityType,
      items,
      summaryReconciled
    );

    const eventWithData: EventWithData = {
      event,
      counts,
      entities,
    };

    catalogResults.events[viagogoVirtualId] = eventWithData;
    catalogResults.viagIdToLatestIdLookup[viagogoVirtualId] = viagogoVirtualId;
    eventsTransformed.push(eventWithData);
  }

  return {
    catalogResults,
    eventsTransformed,
    indexLastUpdatedAt: searchResult.indexLastUpdatedAt,
    totalItemsCount: searchResult.totalItemsCount,
    pitId: searchResult.pitId,
    searchAfter: searchResult.searchAfter,
  };
}

/**
 * Builds CatalogResults and EventWithData[] from SearchDataWithEvent for sales.
 * This extracts embedded catalog data from SaleWithCatalogData objects.
 */
export function buildCatalogAndEventsFromSalesSearchData(
  searchData: EntitySearchResult,
  searchDataSummary: SearchDataSummary | null | undefined,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
} {
  return buildCatalogAndEventsFromSearchData(
    searchData,
    searchDataSummary,
    ActionOutboxEntityType.Sale,
    catalogDataFallback
  );
}

/**
 * Builds CatalogResults and EventWithData[] from SearchDataWithEvent for listings.
 * This extracts embedded catalog data from ListingWithCatalogData objects.
 */
export function buildCatalogAndEventsFromListingsSearchData(
  searchData: EntitySearchResult,
  searchDataSummary: SearchDataSummary | null | undefined,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
} {
  return buildCatalogAndEventsFromSearchData(
    searchData,
    searchDataSummary,
    ActionOutboxEntityType.Listing,
    catalogDataFallback
  );
}
